import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import './sim-add-panel.css';

/**
 * SimAddPanel — боковая панель добавления SIM-карт
 * По макету Figma 26-04 wa Proxy Manager (node 436:73829)
 *
 * Две вкладки: "Вручную" и "Из файла"
 */

// ==================== SVG ICONS ====================

// Иконка закрытия (крестик)
const CloseIcon = () => (
  <svg
    className="sap__close-icon"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M18 6L6 18M6 6L18 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Иконка dropdown (стрелка вниз)
const DropdownIcon = ({ isOpen = false }) => (
  <svg
    className={`sap__dropdown-icon ${isOpen ? 'sap__dropdown-icon--open' : ''}`}
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6 9L12 15L18 9"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Иконка календаря
const CalendarIcon = () => (
  <svg
    className="sap__calendar-icon"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M19 4H5C3.89543 4 3 4.89543 3 6V20C3 21.1046 3.89543 22 5 22H19C20.1046 22 21 21.1046 21 20V6C21 4.89543 20.1046 4 19 4Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M16 2V6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8 2V6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3 10H21"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <rect x="7" y="14" width="2" height="2" fill="currentColor" />
    <rect x="11" y="14" width="2" height="2" fill="currentColor" />
    <rect x="15" y="14" width="2" height="2" fill="currentColor" />
  </svg>
);

// ==================== SELECT COMPONENT ====================

const SelectField = ({
  label,
  value,
  options = [],
  onChange,
  placeholder = 'Выберите...',
  required = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = () => {
    setIsOpen((prev) => {
      const next = !prev;
      if (!next) {
        setSearchText('');
      }
      return next;
    });
  };

  const handleSelect = (optionValue) => {
    if (onChange) {
      onChange(optionValue);
    }
    setSearchText('');
    setIsOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggle();
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const selectedOption = options.find((opt) => opt.value === value);
  const displayValue = selectedOption ? selectedOption.label : placeholder;
  const filteredOptions = options.filter((option) =>
    String(option.label || '').toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <div className="sap__input-group" ref={ref}>
      <div className="sap__input-label">
        {required && <span className="sap__label-required">*</span>}
        <span className="sap__label-text">{label}</span>
      </div>
      <div className="sap__dropdown">
        <div
          className="sap__input-field"
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
          role="button"
          tabIndex={0}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-label={label}
        >
          <span className={`sap__input-value ${value ? 'sap__input-value--filled' : ''}`}>
            {displayValue}
          </span>
          <DropdownIcon isOpen={isOpen} />
        </div>
        {isOpen && (
          <div className="sap__dropdown-list" role="listbox">
            <div className="sap__dropdown-search">
              <input
                type="text"
                className="sap__dropdown-search-input"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Поиск..."
                aria-label={`Поиск: ${label}`}
                autoFocus
              />
            </div>
            {filteredOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`sap__dropdown-option ${value === option.value ? 'sap__dropdown-option--active' : ''}`}
                onClick={() => handleSelect(option.value)}
                role="option"
                aria-selected={value === option.value}
                tabIndex={0}
              >
                {option.label}
              </button>
            ))}
            {filteredOptions.length === 0 && (
              <div className="sap__dropdown-empty">Ничего не найдено</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== TEXT INPUT COMPONENT ====================

const TextField = ({
  label,
  value,
  onChange,
  placeholder = '',
  required = false,
  type = 'text',
  multiline = false,
  rows = 4,
}) => {
  const handleChange = (e) => {
    if (onChange) {
      onChange(e.target.value);
    }
  };

  const inputClassName = ['sap__input-value', value ? 'sap__input-value--filled' : '', multiline ? 'sap__input-value--multiline' : '']
    .filter(Boolean)
    .join(' ');

  return (
    <div className="sap__input-group">
      <div className="sap__input-label">
        {required && <span className="sap__label-required">*</span>}
        <span className="sap__label-text">{label}</span>
      </div>
      <div className={`sap__input-field${multiline ? ' sap__input-field--multiline' : ''}`}>
        {multiline ? (
          <textarea
            className={inputClassName}
            value={value}
            onChange={handleChange}
            placeholder={placeholder}
            aria-label={label}
            rows={rows}
          />
        ) : (
          <input
            type={type}
            className={inputClassName}
            value={value}
            onChange={handleChange}
            placeholder={placeholder}
            aria-label={label}
          />
        )}
      </div>
    </div>
  );
};

// ==================== PERSONAL ACCOUNT SEARCH COMPONENT ====================

const PA_DROPDOWN_MARGIN = 4;
const PA_DROPDOWN_MAX_HEIGHT = 600;
const PA_DROPDOWN_VIEWPORT_PAD = 8;
/** Минимум места снизу, иначе открываем список вверх (форма «Создать ЛК» ~340px) */
const PA_CREATE_FORM_FLIP_THRESHOLD = 360;

const PersonalAccountField = ({
  value,
  onChange,
  operatorOptions = [],
}) => {
  const [searchText, setSearchText] = useState('');
  const [allAccounts, setAllAccounts] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newAccount, setNewAccount] = useState({ name: '', phone_number: '', password: '', operator: '' });
  const [paDropdownStyle, setPaDropdownStyle] = useState(null);
  const ref = useRef(null);
  const paTriggerRef = useRef(null);
  const paDropdownRef = useRef(null);
  const debounceRef = useRef(null);
  const allLoadedRef = useRef(false);

  const updatePaDropdownPosition = useCallback(() => {
    if (!isOpen || !paTriggerRef.current) {
      setPaDropdownStyle(null);
      return;
    }
    const rect = paTriggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - PA_DROPDOWN_MARGIN - PA_DROPDOWN_VIEWPORT_PAD;
    const spaceAbove = rect.top - PA_DROPDOWN_MARGIN - PA_DROPDOWN_VIEWPORT_PAD;
    const openUp =
      showCreateForm &&
      spaceBelow < PA_CREATE_FORM_FLIP_THRESHOLD &&
      spaceAbove > spaceBelow;
    const maxHeight = Math.min(
      PA_DROPDOWN_MAX_HEIGHT,
      Math.max(0, openUp ? spaceAbove : spaceBelow)
    );
    const vw = window.innerWidth;
    const pad = PA_DROPDOWN_VIEWPORT_PAD;
    let width = Math.min(rect.width, vw - 2 * pad);
    let left = rect.left;
    if (left + width > vw - pad) {
      left = vw - pad - width;
    }
    if (left < pad) {
      left = pad;
    }
    if (left + width > vw - pad) {
      width = Math.max(160, vw - left - pad);
    }
    const base = {
      position: 'fixed',
      left,
      width,
      maxWidth: vw - 2 * pad,
      boxSizing: 'border-box',
      maxHeight,
      zIndex: 1300,
      right: 'auto',
      marginTop: 0,
      overflowY: 'auto',
    };
    if (openUp) {
      setPaDropdownStyle({
        ...base,
        top: 'auto',
        bottom: window.innerHeight - rect.top + PA_DROPDOWN_MARGIN,
      });
    } else {
      setPaDropdownStyle({
        ...base,
        top: rect.bottom + PA_DROPDOWN_MARGIN,
        bottom: 'auto',
      });
    }
  }, [isOpen, showCreateForm]);

  useLayoutEffect(() => {
    const showList = isOpen && (searchResults.length > 0 || showCreateForm || isSearching);
    if (!showList) {
      setPaDropdownStyle(null);
      return;
    }
    updatePaDropdownPosition();
  }, [
    isOpen,
    searchResults,
    showCreateForm,
    isSearching,
    updatePaDropdownPosition,
  ]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const handleScrollOrResize = () => {
      updatePaDropdownPosition();
    };
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [isOpen, updatePaDropdownPosition]);

  useEffect(() => {
    if (value) {
      setSearchText(value.name || '');
    } else {
      setSearchText('');
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current?.contains(e.target) || paDropdownRef.current?.contains(e.target)) {
        return;
      }
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadAllAccounts = useCallback(async () => {
    if (allLoadedRef.current && allAccounts.length > 0) return;
    setIsSearching(true);
    try {
      const token = getAuthToken();
      const response = await fetch(
        `${API_BASE_URL}/api/personal-accounts`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setAllAccounts(data);
        allLoadedRef.current = true;
        if (!searchText) {
          setSearchResults(data);
          setShowCreateForm(false);
        }
      }
    } catch (err) {
      // ignore
    } finally {
      setIsSearching(false);
    }
  }, [searchText, allAccounts.length]);

  const handleSearch = useCallback(async (text) => {
    if (!text) {
      setSearchResults(allAccounts);
      setShowCreateForm(false);
      return;
    }
    const lower = text.toLowerCase();
    const filtered = allAccounts.filter(
      (a) =>
        a.name.toLowerCase().includes(lower) ||
        (a.phone_number && a.phone_number.includes(text))
    );
    setSearchResults(filtered);
    setShowCreateForm(filtered.length === 0 && text.length >= 2);
  }, [allAccounts]);

  const handleInputChange = (e) => {
    const text = e.target.value;
    setSearchText(text);
    setIsOpen(true);
    if (onChange) onChange(null);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => handleSearch(text), 150);
  };

  const handleFocus = () => {
    setIsOpen(true);
    loadAllAccounts();
    if (!searchText && allAccounts.length > 0) {
      setSearchResults(allAccounts);
    }
  };

  const handleSelectAccount = (account) => {
    setSearchText(account.name);
    setIsOpen(false);
    setShowCreateForm(false);
    if (onChange) onChange({ type: 'existing', id: account.id, name: account.name, operator: account.operator, phone_number: account.phone_number });
  };

  const handleNewAccountChange = (field, val) => {
    setNewAccount((prev) => ({ ...prev, [field]: val }));
  };

  const handleCreateNew = () => {
    const account = { ...newAccount, name: newAccount.name || searchText };
    if (onChange) onChange({ type: 'new', ...account });
    setIsOpen(false);
    setSearchText(account.name);
  };

  const handleClear = () => {
    setSearchText('');
    setSearchResults(allAccounts);
    setShowCreateForm(false);
    if (onChange) onChange(null);
  };

  return (
    <div className="sap__input-group sap__pa-field" ref={ref}>
      <div className="sap__input-label">
        <span className="sap__label-text">Личный кабинет</span>
      </div>
      <div className="sap__dropdown">
        <div className="sap__input-field" ref={paTriggerRef}>
          <input
            type="text"
            className={`sap__input-value ${searchText ? 'sap__input-value--filled' : ''}`}
            value={searchText}
            onChange={handleInputChange}
            onFocus={handleFocus}
            placeholder="Поиск по имени или номеру..."
            aria-label="Личный кабинет"
            autoComplete="off"
          />
          {searchText && (
            <button type="button" className="sap__clear-btn" onClick={handleClear} aria-label="Очистить" tabIndex={0}>×</button>
          )}
          <DropdownIcon isOpen={isOpen} />
        </div>

        {isOpen && (searchResults.length > 0 || showCreateForm || isSearching) && paDropdownStyle
          && createPortal(
            <div
              ref={paDropdownRef}
              className="sap__dropdown-list sap__pa-dropdown sap__pa-dropdown--fixed"
              role="listbox"
              style={paDropdownStyle}
            >
              <div className="sap__pa-dropdown-scroll">
                {isSearching && <div className="sap__dropdown-loading">Загрузка...</div>}

                {searchResults.map((account) => (
                  <button
                    key={account.id}
                    type="button"
                    className="sap__dropdown-option sap__pa-option"
                    onClick={() => handleSelectAccount(account)}
                    role="option"
                    aria-selected={value?.id === account.id}
                    tabIndex={0}
                  >
                    <span className="sap__pa-name">{account.name}</span>
                    <span className="sap__pa-meta">{account.operator} · {account.phone_number}</span>
                  </button>
                ))}
              </div>

              {showCreateForm && !isSearching && (
                <div className="sap__pa-create">
                  <div className="sap__pa-create-title">Создать новый ЛК</div>
                  <div className="sap__pa-create-field">
                    <label className="sap__pa-create-label">Имя / фамилия</label>
                    <div className="sap__input-field">
                      <input
                        type="text"
                        className={`sap__input-value ${(newAccount.name || searchText) ? 'sap__input-value--filled' : ''}`}
                        placeholder="Введите имя"
                        value={newAccount.name || searchText}
                        onChange={(e) => handleNewAccountChange('name', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="sap__pa-create-field">
                    <label className="sap__pa-create-label">Номер телефона ЛК</label>
                    <div className="sap__input-field">
                      <input
                        type="text"
                        className={`sap__input-value ${newAccount.phone_number ? 'sap__input-value--filled' : ''}`}
                        placeholder="Введите номер"
                        value={newAccount.phone_number}
                        onChange={(e) => handleNewAccountChange('phone_number', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="sap__pa-create-field">
                    <label className="sap__pa-create-label">Пароль от ЛК</label>
                    <div className="sap__input-field">
                      <input
                        type="text"
                        className={`sap__input-value ${newAccount.password ? 'sap__input-value--filled' : ''}`}
                        placeholder="Введите пароль"
                        value={newAccount.password}
                        onChange={(e) => handleNewAccountChange('password', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="sap__pa-create-field">
                    <label className="sap__pa-create-label">Оператор ЛК</label>
                    <div className="sap__input-field">
                      <select
                        className={`sap__input-value sap__pa-select ${newAccount.operator ? 'sap__input-value--filled' : ''}`}
                        value={newAccount.operator}
                        onChange={(e) => handleNewAccountChange('operator', e.target.value)}
                      >
                        <option value="">Выберите оператора</option>
                        {operatorOptions.map((op) => (
                          <option key={op.value} value={op.value}>{op.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="sap__btn sap__btn--add sap__pa-create-btn"
                    onClick={handleCreateNew}
                    disabled={!newAccount.phone_number || !newAccount.password || !newAccount.operator}
                    tabIndex={0}
                  >
                    Создать ЛК
                  </button>
                </div>
              )}
            </div>,
            document.body
          )}
      </div>
    </div>
  );
};

// ==================== DATE INPUT COMPONENT ====================

const DateField = ({
  label,
  value,
  onChange,
  placeholder = '',
}) => {
  const dateInputRef = useRef(null);
  const [textValue, setTextValue] = useState('');

  // Синхронизация textValue с value (формат YYYY-MM-DD -> DD.MM.YYYY)
  useEffect(() => {
    if (value) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        setTextValue(`${day}.${month}.${year}`);
      }
    } else {
      setTextValue('');
    }
  }, [value]);

  // Обработка ввода текста (DD.MM.YYYY)
  const handleTextChange = (e) => {
    const input = e.target.value;
    setTextValue(input);

    // Парсим дату из формата DD.MM.YYYY
    const match = input.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    if (match) {
      const [, day, month, year] = match;
      const isoDate = `${year}-${month}-${day}`;
      if (onChange) {
        onChange(isoDate);
      }
    }
  };

  // Обработка выбора из календаря
  const handleDateChange = (e) => {
    if (onChange) {
      onChange(e.target.value);
    }
  };

  // Открытие календаря по клику на иконку
  const handleOpenCalendar = (e) => {
    e.stopPropagation();
    if (dateInputRef.current) {
      dateInputRef.current.showPicker?.();
    }
  };

  return (
    <div className="sap__input-group">
      <div className="sap__input-label">
        <span className="sap__label-text">{label}</span>
      </div>
      <div className="sap__input-field sap__input-field--date">
        <input
          type="text"
          className={`sap__input-value ${textValue ? 'sap__input-value--filled' : ''}`}
          value={textValue}
          onChange={handleTextChange}
          placeholder={placeholder || 'ДД.ММ.ГГГГ'}
          aria-label={label}
        />
        <input
          ref={dateInputRef}
          type="date"
          className="sap__date-input-hidden"
          value={value}
          onChange={handleDateChange}
          tabIndex={-1}
        />
        <button
          type="button"
          className="sap__calendar-btn"
          onClick={handleOpenCalendar}
          aria-label="Открыть календарь"
          tabIndex={0}
        >
          <CalendarIcon />
        </button>
      </div>
    </div>
  );
};

// ==================== FILE UPLOAD COMPONENT ====================

const FileUploadTab = React.forwardRef(({ onFileSelect }, ref) => {
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file && onFileSelect) {
      onFileSelect(file);
    }
  };

  return (
    <div className="sap__file-upload-wrapper">
      <input
        ref={ref}
        type="file"
        accept=".xlsx,.xls"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
    </div>
  );
});

// API базовый URL:
// - production: только same-origin (CSP connect-src 'self')
// - development: можно переопределить через REACT_APP_API_URL
const API_BASE_URL =
  process.env.NODE_ENV === 'production'
    ? ''
    : (process.env.REACT_APP_API_URL !== undefined ? process.env.REACT_APP_API_URL : '');

// Функция для получения токена
const getAuthToken = () => {
  return localStorage.getItem('token') || sessionStorage.getItem('token') || '';
};

// ==================== MAIN COMPONENT ====================

const SimAddPanel = ({
  isOpen = false,
  onClose,
  onAdd,
  // Опции для дропдаунов
  simStatusOptions = [
    { value: 'working', label: 'Работает' },
    { value: 'free', label: 'Свободен' },
    { value: 'reserve_msk', label: 'Резерв Мск' },
    { value: 'reserve_spb', label: 'Резерв СПб' },
    { value: 'check', label: 'Проверить' },
    { value: 'replace', label: 'Заменить' },
    { value: 'frozen', label: 'Заморожен' },
  ],
  operatorOptions = [
    { value: 'МТС', label: 'МТС' },
    { value: 'Мегафон', label: 'Мегафон' },
    { value: 'Билайн', label: 'Билайн' },
    { value: 'Tele2', label: 'Tele2' },
    { value: 'Yota', label: 'Yota' },
  ],
  registeredByOptions = [],
}) => {
  // Ref для скрытого file input
  const fileInputRef = useRef(null);

  // Активная вкладка: 'manual' или 'file'
  const [activeTab, setActiveTab] = useState('manual');

  // Данные подключений с API
  const [connectionsData, setConnectionsData] = useState({
    connections: [],
    cities: [],
  });

  // Данные формы
  const [formData, setFormData] = useState({
    simStatus: '',
    operator: '',
    connection: '',
    modem: '',
    geo: '',
    phoneNumber: '',
    amount: '',
    paymentDate: '',
    registeredBy: '',
    accountLink: '',
    comment: '',
    imei: '',
    mac: '',
  });

  // Выбранный / новый личный кабинет
  const [selectedPA, setSelectedPA] = useState(null);

  // Загрузка подключений при открытии
  useEffect(() => {
    if (isOpen) {
      const fetchConnections = async () => {
        try {
          const token = getAuthToken();
          const response = await fetch(`${API_BASE_URL}/api/sim-cards/connections`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
          if (response.ok) {
            const data = await response.json();
            setConnectionsData(data);
          }
        } catch (err) {
          // Ошибка загрузки не критична
        }
      };
      fetchConnections();
    }
  }, [isOpen]);

  // Сброс формы при открытии
  useEffect(() => {
    if (isOpen) {
      setFormData({
        simStatus: '',
        operator: '',
        connection: '',
        modem: '',
        geo: '',
        phoneNumber: '',
        amount: '',
        paymentDate: new Date().toISOString().split('T')[0],
        registeredBy: '',
        accountLink: '',
        comment: '',
        imei: '',
        mac: '',
      });
      setActiveTab('manual');
      setSelectedPA(null);
    }
  }, [isOpen]);

  // Опции подключений
  const connectionOptions = connectionsData.connections.map((conn) => ({
    value: conn.id.toString(),
    label: conn.name,
    city: conn.city,
    modems: conn.modems || [],
  }));

  // Опции модемов — фильтруем по выбранному подключению
  const getModemNumberFromInternalIp = (ipAddress) => {
    if (!ipAddress || typeof ipAddress !== 'string') return null;
    const parts = ipAddress.trim().split('.');
    if (parts.length !== 4) return null;
    const octet = Number.parseInt(parts[2], 10);
    if (!Number.isFinite(octet)) return null;
    return octet;
  };

  const modemOptions = (() => {
    if (!formData.connection) return [];
    const selectedConn = connectionsData.connections.find(
      (c) => c.id.toString() === formData.connection
    );
    if (!selectedConn || !selectedConn.modems) return [];
    return selectedConn.modems.map((m) => {
      const modemNumber = getModemNumberFromInternalIp(m.modem_internal_ip);
      return {
        value: m.id.toString(),
        label: modemNumber != null
          ? `№ ${modemNumber} ${m.name || ''}`.trim()
          : (m.name || String(m.id)),
        modemNumber,
      };
    });
  })();

  // ГЕО вычисляется автоматически из выбранного подключения
  const computedGeo = (() => {
    if (!formData.connection) return '';
    const selectedConn = connectionsData.connections.find(
      (c) => c.id.toString() === formData.connection
    );
    return selectedConn?.city || '';
  })();

  // Обновление поля с логикой связи Подключение → Модем → ГЕО
  const handleFieldChange = useCallback((field, value) => {
    setFormData((prev) => {
      const newData = { ...prev, [field]: value };

      // При выборе подключения — автозаполняем ГЕО и сбрасываем модем
      if (field === 'connection' && value) {
        const selectedConn = connectionsData.connections.find(
          (c) => c.id.toString() === value
        );
        if (selectedConn) {
          newData.geo = selectedConn.city || '';
          newData.modem = ''; // Сбрасываем выбор модема
        }
      }

      // При сбросе подключения — сбрасываем модем и ГЕО
      if (field === 'connection' && !value) {
        newData.modem = '';
        newData.geo = '';
      }

      return newData;
    });
  }, [connectionsData.connections]);

  // Закрытие
  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  const handleAdd = useCallback(() => {
    if (onAdd) {
      const selectedConnection = connectionsData.connections.find(
        (conn) => conn.id.toString() === formData.connection
      );
      const selectedModem = selectedConnection?.modems?.find(
        (modem) => modem.id.toString() === formData.modem
      );
      const modemNumber = selectedModem
        ? getModemNumberFromInternalIp(selectedModem.modem_internal_ip)
        : null;
      const payload = {
        ...formData,
        geo: computedGeo,
        serverOption: selectedConnection?.name || null,
        modemOption: modemNumber != null ? `№ ${modemNumber}` : null,
      };

      if (selectedPA) {
        if (selectedPA.type === 'existing') {
          payload.personalAccountId = selectedPA.id;
        } else if (selectedPA.type === 'new') {
          payload.newPersonalAccount = {
            name: selectedPA.name,
            phone_number: selectedPA.phone_number,
            password: selectedPA.password,
            operator: selectedPA.operator,
          };
        }
      }

      onAdd(payload);
    }
    handleClose();
  }, [onAdd, formData, computedGeo, selectedPA, handleClose, connectionsData.connections]);

  // Загрузка файла
  const handleFileSelect = useCallback((file) => {
    // Здесь будет логика обработки файла
    if (onAdd) {
      onAdd({ file, type: 'file_upload' });
    }
    handleClose();
  }, [onAdd, handleClose]);

  // Закрытие по Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleClose]);

  // Клик по оверлею
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Проверка заполненности обязательных полей
  const isFormValid = formData.simStatus !== '';

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="sap-overlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="sap-title"
    >
      <div className="sap">
        {/* Header */}
        <div className="sap__header">
          <h2 className="sap__title" id="sap-title">
            добавление сим-карт
          </h2>
          <button
            type="button"
            className="sap__close-btn"
            onClick={handleClose}
            aria-label="Закрыть"
            tabIndex={0}
          >
            <CloseIcon />
          </button>
        </div>

        {/* Content */}
        <div className="sap__content">
          {/* Segment Control */}
          <div className="sap__segment">
            <button
              type="button"
              className={`sap__segment-item ${activeTab === 'manual' ? 'sap__segment-item--active' : ''}`}
              onClick={() => setActiveTab('manual')}
              tabIndex={0}
            >
              Вручную
            </button>
            <button
              type="button"
              className={`sap__segment-item ${activeTab === 'file' ? 'sap__segment-item--active' : ''}`}
              onClick={() => setActiveTab('file')}
              tabIndex={0}
            >
              Из файла
            </button>
          </div>

          {activeTab === 'manual' && (
            <>
              {/* Строка 1: Статус SIM + Оператор */}
              <div className="sap__row">
                <SelectField
                  label="Статус SIM"
                  value={formData.simStatus}
                  options={simStatusOptions}
                  onChange={(val) => handleFieldChange('simStatus', val)}
                  placeholder="Выберите статус"
                  required
                />
                <SelectField
                  label="Оператор"
                  value={formData.operator}
                  options={operatorOptions}
                  onChange={(val) => handleFieldChange('operator', val)}
                  placeholder="Выберите оператора"
                />
              </div>

              {/* Строка 2: Подключение + Модем */}
              <div className="sap__row">
                <SelectField
                  label="Подключение"
                  value={formData.connection}
                  options={connectionOptions}
                  onChange={(val) => handleFieldChange('connection', val)}
                  placeholder="Выберите подключение"
                />
                <SelectField
                  label="Модем"
                  value={formData.modem}
                  options={modemOptions}
                  onChange={(val) => handleFieldChange('modem', val)}
                  placeholder={formData.connection ? "Выберите модем" : "Сначала выберите подключение"}
                />
              </div>

              {/* Строка 3: ГЕО + Номер телефона */}
              <div className="sap__row">
                <TextField
                  label="ГЕО"
                  value={computedGeo}
                  onChange={() => {}}
                  placeholder="Заполняется автоматически"
                />
                <TextField
                  label="Номер"
                  value={formData.phoneNumber}
                  onChange={(val) => handleFieldChange('phoneNumber', val)}
                  placeholder="Один номер или несколько — каждый с новой строки или через запятую"
                  multiline
                  rows={4}
                />
              </div>

              {/* Строка 4: Сумма платежа + Дата платежа */}
              <div className="sap__row">
                <TextField
                  label="Сумма платежа"
                  value={formData.amount}
                  onChange={(val) => handleFieldChange('amount', val)}
                  placeholder="Введите сумму платежа"
                  type="number"
                />
                <DateField
                  label="Дата платежа"
                  value={formData.paymentDate}
                  onChange={(val) => handleFieldChange('paymentDate', val)}
                />
              </div>

              {/* Личный кабинет — поиск + создание */}
              <PersonalAccountField
                value={selectedPA}
                onChange={setSelectedPA}
                operatorOptions={operatorOptions}
              />

              <TextField
                label="Оформлена"
                value={formData.registeredBy}
                onChange={(val) => handleFieldChange('registeredBy', val)}
                placeholder="Введите на кого оформлена"
              />

              {/* IMEI + MAC */}
              <div className="sap__row">
                <TextField
                  label="IMEI"
                  value={formData.imei}
                  onChange={(val) => handleFieldChange('imei', val)}
                  placeholder="IMEI модема"
                />
                <TextField
                  label="MAC"
                  value={formData.mac}
                  onChange={(val) => handleFieldChange('mac', val)}
                  placeholder="MAC-адрес модема"
                />
              </div>

              {/* Комментарий */}
              <TextField
                label="Комментарий"
                value={formData.comment}
                onChange={(val) => handleFieldChange('comment', val)}
                placeholder="Введите комментарий"
              />
            </>
          )}

          {/* Вкладка "Из файла": скрытый input для загрузки */}
          {activeTab === 'file' && (
            <FileUploadTab ref={fileInputRef} onFileSelect={handleFileSelect} />
          )}
        </div>

        {/* Footer — всегда отображается */}
        <div className="sap__footer">
          <button
            type="button"
            className="sap__btn sap__btn--cancel"
            onClick={handleClose}
            tabIndex={0}
          >
            Отмена
          </button>
          {activeTab === 'manual' ? (
            <button
              type="button"
              className="sap__btn sap__btn--add"
              onClick={handleAdd}
              disabled={!isFormValid}
              tabIndex={0}
            >
              Сохранить
            </button>
          ) : (
            <button
              type="button"
              className="sap__btn sap__btn--add"
              onClick={() => fileInputRef?.current?.click?.()}
              tabIndex={0}
            >
              Загрузить файл
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SimAddPanel;
